import { apiUrl } from '@ai-studio/shared';

import { WEBSITE } from './edition.js';

/**
 * HTTP client.
 *
 * Every URL goes through `apiUrl`, which applies the app's mount path. Nothing
 * here may hardcode a leading slash or the app breaks the moment it is served
 * from a subdirectory instead of a domain root.
 */

let csrfToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

/** Called when the server reports the session is gone, so the UI can react. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly body: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const MUTATIONS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  // The website answers engine jobs (model import / convert, graph compile,
  // impostor bake) in the visitor's browser instead of on its server.
  if (WEBSITE && method === 'POST' && /^\/projects\/\d+\/(models|graph|impostors|procgen|sound)\//.test(path)) {
    const { browserJob } = await import('./browser-jobs.js');
    try {
      const answered = await browserJob<T>((m, p, b) => send(m, p, b), method, path, body);
      if (answered !== undefined) return answered;
    } catch (error) {
      if (error instanceof ApiError) throw error;
      const status = (error as { status?: number }).status ?? 422;
      throw new ApiError(status, 'browser_job', error instanceof Error ? error.message : String(error));
    }
  }
  return send<T>(method, path, body, signal);
}

async function send<T>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (body !== undefined) headers['Content-Type'] = 'application/json';

  // Double-submit CSRF: the token is bound to the HttpOnly session cookie.
  if (MUTATIONS.has(method.toUpperCase()) && csrfToken) {
    headers['x-ai-studio-csrf'] = csrfToken;
  }

  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      method,
      headers,
      // Same-origin keeps the session cookie scoped and avoids CORS entirely.
      credentials: 'same-origin',
      body: body === undefined ? undefined : JSON.stringify(body),
      ...(signal ? { signal } : {}),
    });
  } catch (error) {
    if ((error as Error)?.name === 'AbortError') throw error;
    throw new ApiError(0, 'network', 'Could not reach the server.');
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!response.ok) {
    const record = (payload ?? {}) as { error?: string; message?: string };
    if (response.status === 401 && !path.startsWith('/auth/login')) {
      onUnauthorized?.();
    }
    throw new ApiError(
      response.status,
      record.error ?? 'error',
      record.message ?? `Request failed (${response.status}).`,
      payload,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>('GET', path, undefined, signal),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
};